import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    Injector,
    NgZone,
    OnDestroy,
    OnInit,
    afterNextRender,
    inject,
  } from '@angular/core';
  import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
  import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
  import { marked, parseInline } from 'marked';
  import { environment } from '@myenv/environment';
  import { KnowledgeBaseService } from './knowledge-base.service';
  import {
    KbArticle,
    KbContentBlock,
    KbDataTableBlock,
    KbSection,
  } from './knowledge-base.models';
  
  type ViewMode = 'home' | 'section';
  
  /** Interleaves markdown body and structured blocks for template rendering. */
  type KbArticleRenderSlice =
    | { kind: 'markdown' }
    | { kind: 'blocks'; items: KbContentBlock[] };
  
  @Component({
    selector: 'app-knowledge-base',
    templateUrl: './knowledge-base.component.html',
    styleUrls: ['./knowledge-base.shell.scss', './knowledge-base.content.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
  })
  export class KnowledgeBaseComponent implements OnInit, OnDestroy {
    private readonly kb = inject(KnowledgeBaseService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    private readonly ngZone = inject(NgZone);

    sections: KbSection[] = [];
    loadError: string | null = null;
  
    view: ViewMode = 'home';
    sectionIndex: number | null = null;
    currentSection: KbSection | null = null;
  
    expandedAccordionKey: string | null = null;
    activePath: {
      subsectionIndex?: number;
      nestedIndex?: number;
      articleIndex: number;
      subArticleIndex?: number;
    } | null = null;
  
    selectedArticle: KbArticle | null = null;
  
    pdfLoading = false;
    pdfError: string | null = null;
    pdfPreviewUrl: SafeResourceUrl | null = null;
    private pdfBlobUrl: string | null = null;
    private pdfLoadAbort: AbortController | null = null;
  
    searchQuery = '';
    searchInputFocused = false;
    aiLoading = false;
    aiError: string | null = null;
    aiSummaryHtml: SafeHtml | null = null;
    lastAiQuery = '';
    /** Invalidates in-flight AI responses when the user clears search or submits a new query. */
    private aiSearchSeq = 0;
    /** Precomputed when an article is selected — avoids rebuilding slices on every CD cycle. */
    cachedArticleSlices: KbArticleRenderSlice[] = [];
    private readonly sectionIconCache = new Map<string, SafeHtml>();
    private readonly htmlCache = new Map<string, SafeHtml>();
    private readonly dataTableGroupRowsCache = new WeakMap<
      KbDataTableBlock,
      {
        key: string;
        groupLabel?: string;
        groupRowspan?: number;
        cellItems: { id: string; text: string }[];
      }[]
    >();

    readonly cardGroups = [
      { title: 'Getting Started', sectionIndices: [0, 1, 2] },
      { title: 'Device Connectivity', sectionIndices: [3, 4] },
      { title: 'Resources', sectionIndices: [5, 6, 7, 8] },
    ] as const;
  
    readonly cardDescriptions: Record<string, string> = {
      Products: 'Browse Danlaw Product Offerings',
      'Device Onboarding': 'Setup, installation, and configuration guides',
      'BitBrew Platform': 'Device management, events, and data integration',
      Communication: 'Cellular, Wi‑Fi, and Bluetooth connectivity',
      Troubleshooting: 'Device health, faults, and diagnostics',
      Articles: 'Frequently asked questions and how‑tos',
      'Hardware Datasheets': 'Product datasheets and specifications',
      Cables: 'Cable types, part numbers, and reference images',
      'Release Notes': 'Latest updates and version history',
    };
  
    private readonly cardIconByTitle: Record<string, string> = {
      Products: 'car',
      'Device Onboarding': 'clipboardCheck',
      'BitBrew Platform': 'dashboard',
      Communication: 'signal',
      Troubleshooting: 'alertTriangle',
      Articles: 'book',
      'Hardware Datasheets': 'fileDown',
      Cables: 'cable',
      'Release Notes': 'megaphone',
    };
  
    private readonly iconSvg: Record<string, string> = {
      signal:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20h.01M7 20v-5m4 5v-8m4 8V4m5 4h.01M22 20h.01"/></svg>',
      car: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M5 17h14v-5H5v5z"/><path d="M5 12l2-4h10l2 4"/><circle cx="7.5" cy="17" r="1.5"/><circle cx="16.5" cy="17" r="1.5"/></svg>',
      heart:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
      doc: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
      book: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="16" y2="10"/></svg>',
      fileDown:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M12 11v6"/><path d="M9 14l3 3 3-3"/></svg>',
      megaphone:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>',
      cable:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14a2 2 0 0 1 0-4"/><path d="M20 14a2 2 0 0 0 0-4"/><path d="M6 12h2l1.5-3h7l1.5 3h2"/><circle cx="4" cy="12" r="2"/><circle cx="20" cy="12" r="2"/></svg>',
      clipboardCheck:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="m9 14 2 2 4-4"/></svg>',
      dashboard:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>',
      alertTriangle:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
    };
  
    readonly contactMailto =
      'mailto:' + (environment.contactEmail || 'support@example.com');
  
    readonly copyMarkdownLabel = 'Copy as Markdown';
  
    constructor() {
      marked.setOptions({ gfm: true, breaks: false });
    }

    ngOnInit(): void {
      this.kb
        .getSections()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            this.sections = data;
            this.loadError = null;
            this.rebuildSectionIconCache();
            this.cdr.markForCheck();
          },
          error: () => {
            this.loadError = 'Could not load knowledge base content.';
            this.cdr.markForCheck();
          },
        });
    }

    ngOnDestroy(): void {
      this.resetPdfPreview();
    }

    cardIconSvg(sec: KbSection): SafeHtml {
      return (
        this.sectionIconCache.get(sec.title) ??
        this.sanitizer.bypassSecurityTrustHtml(this.iconSvg.doc)
      );
    }

    private rebuildSectionIconCache(): void {
      this.sectionIconCache.clear();
      for (const sec of this.sections) {
        const key = this.cardIconByTitle[sec.title] || sec.icon || 'doc';
        const svg = this.iconSvg[key] || this.iconSvg.doc;
        this.sectionIconCache.set(
          sec.title,
          this.sanitizer.bypassSecurityTrustHtml(svg)
        );
      }
    }
  
    sectionArticleCount(sec: KbSection): number {
      if (!sec.articles?.length) return 0;
      return sec.articles.reduce((n, art) => {
        return n + 1 + (art.subArticles?.length ?? 0);
      }, 0);
    }
  
    cardDescription(sec: KbSection): string {
      return (
        this.cardDescriptions[sec.title] ||
        (sec.subsections?.length
          ? `Browse ${sec.subsections.length} subsections`
          : `View ${this.sectionArticleCount(sec)} articles`)
      );
    }
  
    accordionKey(
      subIdx: number,
      kind: 'nested' | 'multi' | 'single'
    ): string {
      return `${kind}-${subIdx}`;
    }
  
    isAccordionExpanded(key: string): boolean {
      return this.expandedAccordionKey === key;
    }
  
    toggleAccordion(key: string): void {
      this.expandedAccordionKey =
        this.expandedAccordionKey === key ? null : key;
      this.cdr.markForCheck();
    }
  
    openHome(): void {
      this.view = 'home';
      this.sectionIndex = null;
      this.currentSection = null;
      this.selectedArticle = null;
      this.activePath = null;
      this.cachedArticleSlices = [];
      this.resetPdfPreview();
      this.scrollKbViewportToTop();
      this.cdr.markForCheck();
    }
  
    /** Layout scrolls `#sm-container`, not the window; reset when KB view changes in-place. */
    private scrollKbViewportToTop(): void {
      document.getElementById('sm-container')?.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      window.scrollTo(0, 0);
    }
  
    openSection(idx: number): void {
      const sec = this.sections[idx];
      if (!sec) return;
      this.scrollKbViewportToTop();
      this.view = 'section';
      this.sectionIndex = idx;
      this.currentSection = sec;
      this.resetPdfPreview();
      this.cachedArticleSlices = [];
  
      if (sec.subsections?.length) {
        const first = this.getFirstArticle(sec);
        if (first) {
          this.expandedAccordionKey = first.accordionKey;
          this.pickArticle(
            first.article,
            {
              subsectionIndex: first.subsectionIndex,
              nestedIndex: first.nestedIndex,
              articleIndex: first.articleIndex,
            },
            false
          );
        } else {
          this.expandedAccordionKey = null;
          this.selectedArticle = null;
          this.activePath = null;
        }
        this.cdr.markForCheck();
        return;
      }
  
      const arts = sec.articles || [];
      if (arts.length > 0) {
        this.expandedAccordionKey = 'flat';
        this.pickArticle(arts[0], { articleIndex: 0 }, false);
      } else {
        this.expandedAccordionKey = null;
        this.selectedArticle = null;
        this.activePath = null;
      }
      this.cdr.markForCheck();
    }
  
    private getFirstArticle(sec: KbSection): {
      article: KbArticle;
      subsectionIndex?: number;
      nestedIndex?: number;
      articleIndex: number;
      accordionKey: string;
    } | null {
      if (!sec.subsections?.length) return null;
      const sub = sec.subsections[0];
      if (sub.subsections?.length) {
        const nested = sub.subsections[0];
        const art = nested.articles?.[0];
        if (!art) return null;
        return {
          article: art,
          subsectionIndex: 0,
          nestedIndex: 0,
          articleIndex: 0,
          accordionKey: this.accordionKey(0, 'nested'),
        };
      }
      const art = sub.articles?.[0];
      if (!art) return null;
      const kind = sub.articles!.length === 1 ? 'single' : 'multi';
      return {
        article: art,
        subsectionIndex: 0,
        articleIndex: 0,
        accordionKey: this.accordionKey(0, kind),
      };
    }
  
    getArticleByPath(
      sectionIndex: number,
      subsectionIndex: number | undefined,
      nestedIndex: number | undefined,
      articleIndex: number,
      subArticleIndex?: number
    ): KbArticle | null {
      const sec = this.sections[sectionIndex];
      if (!sec) return null;
      if (
        nestedIndex !== undefined &&
        subsectionIndex !== undefined
      ) {
        const sub = sec.subsections?.[subsectionIndex];
        const nested = sub?.subsections?.[nestedIndex];
        const arts = nested?.articles;
        const a = arts?.[articleIndex];
        return a ?? null;
      }
      if (subsectionIndex !== undefined) {
        const sub = sec.subsections?.[subsectionIndex];
        const arts = sub?.articles;
        const a = arts?.[articleIndex];
        return a ?? null;
      }
      const arts = sec.articles;
      if (!arts || arts[articleIndex] == null) return null;
      if (subArticleIndex !== undefined) {
        const subs = arts[articleIndex].subArticles;
        return subs?.[subArticleIndex] ?? null;
      }
      return arts[articleIndex];
    }
  
    pickArticle(
      article: KbArticle,
      path: {
        subsectionIndex?: number;
        nestedIndex?: number;
        articleIndex: number;
        subArticleIndex?: number;
      },
      revokePdf = true
    ): void {
      if (revokePdf) {
        this.resetPdfPreview();
      }
      this.scrollKbViewportToTop();
      this.selectedArticle = article;
      this.activePath = { ...path };
      this.cachedArticleSlices = this.articleRenderSlices(article);

      if (article.pdfUrl) {
        this.loadPdfPreview(article.pdfUrl);
      } else {
        this.pdfLoading = false;
        this.pdfError = null;
        this.cdr.markForCheck();
      }
    }
  
    onSidenavLink(
      secIdx: number,
      subIdx: number | undefined,
      artIdx: number,
      nestedIdx?: number,
      subArtIdx?: number
    ): void {
      const article = this.getArticleByPath(
        secIdx,
        subIdx,
        nestedIdx,
        artIdx,
        subArtIdx
      );
      if (!article) return;
      this.pickArticle(article, {
        subsectionIndex: subIdx,
        nestedIndex: nestedIdx,
        articleIndex: artIdx,
        subArticleIndex: subArtIdx,
      });
    }
  
    isLinkActive(
      subIdx: number | undefined,
      artIdx: number,
      nestedIdx?: number,
      subArtIdx?: number
    ): boolean {
      if (!this.activePath || this.sectionIndex === null) return false;
      const p = this.activePath;
      if (subIdx === undefined) {
        return (
          p.subsectionIndex === undefined &&
          p.articleIndex === artIdx &&
          (subArtIdx === undefined
            ? p.subArticleIndex === undefined
            : p.subArticleIndex === subArtIdx)
        );
      }
      return (
        p.subsectionIndex === subIdx &&
        p.articleIndex === artIdx &&
        (nestedIdx === undefined
          ? p.nestedIndex === undefined
          : p.nestedIndex === nestedIdx) &&
        p.subArticleIndex === undefined
      );
    }
  
    markdownHtml(text: string | undefined): SafeHtml {
      const key = `block:${text ?? ''}`;
      const cached = this.htmlCache.get(key);
      if (cached) return cached;
      if (!text?.trim()) {
        const empty = this.sanitizer.bypassSecurityTrustHtml('');
        this.htmlCache.set(key, empty);
        return empty;
      }
      const html = marked.parse(text) as string;
      const safe = this.sanitizer.bypassSecurityTrustHtml(html);
      this.htmlCache.set(key, safe);
      return safe;
    }

    /** Inline markdown only (bold, links, etc.) for table cells. */
    markdownInlineHtml(text: string | undefined): SafeHtml {
      const key = `inline:${text ?? ''}`;
      const cached = this.htmlCache.get(key);
      if (cached) return cached;
      if (!text?.trim()) {
        const empty = this.sanitizer.bypassSecurityTrustHtml('');
        this.htmlCache.set(key, empty);
        return empty;
      }
      const html = parseInline(text.trim()) as string;
      const safe = this.sanitizer.bypassSecurityTrustHtml(html);
      this.htmlCache.set(key, safe);
      return safe;
    }
  
    /**
     * Flatten grouped rows for template (single @for; nested track cannot use outer loop vars).
     */
    expandedDataTableGroupRows(block: KbDataTableBlock): {
      key: string;
      groupLabel?: string;
      groupRowspan?: number;
      /** Value + stable id for @for track (inner loop cannot use outer row ref). */
      cellItems: { id: string; text: string }[];
    }[] {
      const cached = this.dataTableGroupRowsCache.get(block);
      if (cached) return cached;
      const groups = block.groupedRows;
      if (!groups?.length) return [];
      const out: {
        key: string;
        groupLabel?: string;
        groupRowspan?: number;
        cellItems: { id: string; text: string }[];
      }[] = [];
      for (const g of groups) {
        g.rows.forEach((cells, ri) => {
          const rowKey = `${g.group}\u0000${ri}\u0000${cells[0]}`;
          out.push({
            key: rowKey,
            groupLabel: ri === 0 ? g.group : undefined,
            groupRowspan: ri === 0 ? g.rows.length : undefined,
            cellItems: cells.map((text, ci) => ({
              id: `${rowKey}-${ci}`,
              text,
            })),
          });
        });
      }
      this.dataTableGroupRowsCache.set(block, out);
      return out;
    }
  
    /** Build ordered slices: blocks-first articles show tables before prose. */
    articleRenderSlices(article: KbArticle | null): KbArticleRenderSlice[] {
      if (!article) return [];
      const items = article.blocks ?? [];
      const hasMd = !!article.a?.trim();
      const blocksFirst = !!article.blocksFirst && items.length > 0;
      const slices: KbArticleRenderSlice[] = [];
      if (blocksFirst) slices.push({ kind: 'blocks', items });
      if (hasMd) slices.push({ kind: 'markdown' });
      if (!blocksFirst && items.length) slices.push({ kind: 'blocks', items });
      return slices;
    }
  
    /** Full article text for copy-to-clipboard, including structured blocks as markdown tables. */
    articleMarkdownForCopy(article: KbArticle): string {
      const mdBlocks: string[] = [];
      if (article.blocks?.length) {
        for (const block of article.blocks) {
          mdBlocks.push('', ...this.blockToMarkdownLines(block));
        }
      }
      const body = article.a?.trim() ?? '';
      const joined = article.blocksFirst
        ? [...mdBlocks, body].filter(Boolean).join('\n').trim()
        : [body, ...mdBlocks].filter(Boolean).join('\n').trim();
      return joined;
    }
  
    private escapeTableCell(value: string): string {
      return value.replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
    }
  
    private blockToMarkdownLines(block: KbContentBlock): string[] {
      if (block.type === 'specTable') {
        const h0 = block.headers?.[0] ?? 'Area';
        const h1 = block.headers?.[1] ?? 'Details';
        const lines = [`### ${block.title}`, ''];
        if (block.releaseDate?.trim()) {
          lines.push(`**Release date:** ${this.escapeTableCell(block.releaseDate.trim())}`, '');
        }
        lines.push(`| ${h0} | ${h1} |`, '| --- | --- |');
        for (const row of block.rows) {
          lines.push(
            `| **${this.escapeTableCell(row.area)}** | ${this.escapeTableCell(row.details)} |`
          );
        }
        return lines;
      }
      const lines: string[] = [];
      if (block.title?.trim()) {
        lines.push(`### ${block.title.trim()}`, '');
      }
      const cols = block.columns;
      lines.push(
        '| ' + cols.map((c) => this.escapeTableCell(c)).join(' | ') + ' |',
        '| ' + cols.map(() => '---').join(' | ') + ' |'
      );
      const flatRows: string[][] = [];
      if (block.groupedRows?.length) {
        for (const g of block.groupedRows) {
          for (const r of g.rows) {
            flatRows.push([g.group, ...r]);
          }
        }
      } else if (block.rows?.length) {
        flatRows.push(...block.rows);
      }
      for (const row of flatRows) {
        const cells = cols.map((_, i) =>
          this.escapeTableCell(row[i] ?? '')
        );
        lines.push('| ' + cells.join(' | ') + ' |');
      }
      return lines;
    }
  
    cableMarkdown(article: KbArticle): string {
      const lines: string[] = [];
      lines.push('# ' + (article.q || 'Cable'));
      if (article.cableType) lines.push('**Type:** ' + article.cableType);
      if (article.partNumber) lines.push('**Part number:** ' + article.partNumber);
      if (article.imageUrl) lines.push('**Image:** ' + article.imageUrl);
      if (article.a?.trim()) {
        lines.push('');
        lines.push(article.a.trim());
      }
      return lines.join('\n');
    }
  
    copyText(text: string, ev?: Event): void {
      if (!text?.trim()) return;
      const btn =
        ev?.currentTarget instanceof HTMLButtonElement
          ? ev.currentTarget
          : undefined;
      const done = () => {
        if (btn) {
          btn.title = 'Copied!';
          setTimeout(() => (btn.title = this.copyMarkdownLabel), 2000);
        }
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(done);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
          done();
        } catch {
          /* ignore */
        }
        document.body.removeChild(ta);
      }
    }
  
    private resetPdfPreview(): void {
      this.pdfLoadAbort?.abort();
      this.pdfLoadAbort = null;
      if (this.pdfBlobUrl) {
        URL.revokeObjectURL(this.pdfBlobUrl);
        this.pdfBlobUrl = null;
      }
      this.pdfPreviewUrl = null;
    }

    private loadPdfPreview(url: string): void {
      this.resetPdfPreview();
      const abort = new AbortController();
      this.pdfLoadAbort = abort;
      this.pdfLoading = true;
      this.pdfError = null;
      this.cdr.markForCheck();

      afterNextRender(
        () => {
          void this.fetchAndShowPdf(url, abort);
        },
        { injector: this.injector }
      );
    }

    private async fetchAndShowPdf(
      url: string,
      abort: AbortController
    ): Promise<void> {
      try {
        const res = await fetch(url, { signal: abort.signal });
        if (abort.signal.aborted) return;
        if (!res.ok) {
          throw new Error('pdf fetch failed');
        }
        const data = await res.arrayBuffer();
        if (!data?.byteLength) {
          throw new Error('empty pdf');
        }
        if (abort.signal.aborted) return;

        this.pdfBlobUrl = URL.createObjectURL(
          new Blob([data], { type: 'application/pdf' })
        );
        const previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.pdfBlobUrl
        );

        this.ngZone.run(() => {
          if (abort.signal.aborted) return;
          this.pdfPreviewUrl = previewUrl;
          this.pdfLoading = false;
          this.pdfError = null;
          this.cdr.markForCheck();
        });
      } catch (err) {
        if (abort.signal.aborted) return;
        this.ngZone.run(() => {
          this.pdfError =
            'Could not load PDF preview. Use Download if the file is available.';
          this.pdfPreviewUrl = null;
          this.pdfLoading = false;
          this.cdr.markForCheck();
        });
      }
    }

    pdfDownloadHref(): string {
      return this.pdfBlobUrl || this.selectedArticle?.pdfUrl || '#';
    }

    onSearchInput(value: string): void {
      this.searchQuery = value;
      this.cdr.markForCheck();
    }

    onSearchFocus(): void {
      this.searchInputFocused = true;
      this.cdr.markForCheck();
    }

    onSearchBlur(): void {
      this.searchInputFocused = false;
      this.cdr.markForCheck();
    }

    clearSearch(): void {
      this.aiSearchSeq++;
      this.searchQuery = '';
      this.aiLoading = false;
      this.aiError = null;
      this.aiSummaryHtml = null;
      this.lastAiQuery = '';
      this.cdr.markForCheck();
    }
  
    submitSearch(): void {
      const q = this.searchQuery.trim();
      if (!q) return;
      const seq = ++this.aiSearchSeq;
      this.scrollKbViewportToTop();
      this.aiLoading = true;
      this.aiError = null;
      this.aiSummaryHtml = null;
      this.lastAiQuery = q;
      this.kb.aiSearch(q).subscribe({
        next: (data) => {
          if (seq !== this.aiSearchSeq) return;
          const summary = (data.summary || '').trim();
          this.aiSummaryHtml = summary
            ? this.markdownHtml(summary)
            : this.sanitizer.bypassSecurityTrustHtml('<p>No answer.</p>');
          this.aiLoading = false;
          this.cdr.markForCheck();
        },
        error: (err: Error) => {
          if (seq !== this.aiSearchSeq) return;
          this.aiLoading = false;
          this.aiError =
            err?.message ||
            'Search failed. Check that the AI backend is running.';
          this.cdr.markForCheck();
        },
      });
    }
  
  }